<!-- TEMPLATE -->
<template>
  <!-- Password Strength: begins -->
  <div class="password-strength" :class="checkStrength(password)">
    <div class="box">
      <div class="bar-text">{{ text }}</div>
      <div class="bar"></div>
    </div>
  </div>
  <!-- Password Strength: ends -->
</template>

<!-- SCRIPT -->
<script lang="ts">
// VUE
import Vue from 'vue';
import { Component, Prop, Emit, Watch } from 'vue-property-decorator';

import detailsIcon from '../svg/feather-icons/eye.svg';
import forbiddenIcon from '../svg/feather-icons/slash.svg';

// COMPONENTS
import deanIcon from './deanIcon.vue';

@Component({
  components: {
    deanIcon
  }
})
export default class deanPasswordStrength extends Vue {
  // PROPS
  @Prop() password!: string;

  // DATA
  text: string = 'Sterkte';

  checkStrength(password: string) {
    let length = 0;
    // if the password length is 0
    if (password.length == 0) {
      length = 0;
    }
    // if length is 4 characters or more, increase strength value
    else if (password.length <= 4) {
      length = 1;
    }
    //if length is 9 characters or more, increase strength value
    else if (password.length <= 9) {
      length = 2;
    }
    // if length is 9 characters or more, increase strength value
    else if (password.length > 9) {
      length = 3;
    }

    let strength = 0;
    //if password contains both lower and uppercase characters, increase strength value
    if (password.match(/([a-z].*[A-Z])|([A-Z].*[a-z])/)) {
      strength += 1;
    }
    //if password contains numbers, increase strength value
    if (
      password.match(
        /([0-9].*[A-Z])|([0-9].*[a-z])|([a-z].*[0-9])|([A-Z].*[0-9])/
      )
    ) {
      strength += 1;
    }
    //if it has one special character, increase strength value
    if (password.match(/([!,%,&,@,#,$,^,*,?,_,~])/)) {
      strength += 1;
    }
    //if it has two special characters, increase strength value
    if (
      password.match(/(.*[!,%,&,@,#,$,^,*,?,_,~].*[!,",%,&,@,#,$,^,*,?,_,~])/)
    ) {
      strength += 1;
    }
    //now we have calculated strength value, we can return messages

    if (length == 0) {
      this.text = 'sterkte';
      return '';
    } else if (length == 1) {
      this.text = 'Te kort';
      return 'short';
    } else if (length == 2) {
      this.text = 'Zwak';
      return 'weak';
    } else if (length == 3) {
      if (strength <= 1) {
        this.text = 'Redelijk';
        return 'medium';
      } else if (strength == 2) {
        this.text = 'Normaal';
        return 'normal';
      } else if (strength == 3) {
        this.text = 'Sterk';
        return 'strong';
      } else if (strength > 3) {
        this.text = 'Heel sterk';
        return 'very-strong';
      }
    }
  }
}
</script>

<!-- STYLE -->
<style lang="scss" scoped>
// IMPORT
@import '../scss/vars';

.password-strength {
  display: block;
  position: relative;
  max-width: 100%;
  height: 50px;
  padding: 10px;
  font-size: 0px;
  top: 2px;
}

.box {
  position: relative;
  width: 100%;
  height: 30px;
  display: inline-block;
  background-color: var(--color-light-shade-4, $color-light-shade-4);
  text-align: center;
  @include border-radius(100px);
  box-shadow: inset 0 1px 1px rgba(182, 182, 182, 0.2);

  .bar {
    position: absolute;
    width: 0%;
    height: 100%;
    width: 0%;
    z-index: 2;
    -moz-transition: 1s;
    -o-transition: 1s;
    -webkit-transition: 1s;
    transition: 1s;
    @include border-radius(100px);
    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background-image: linear-gradient(
        -45deg,
        rgba(255, 255, 255, 0.1) 25%,
        transparent 25%,
        transparent 50%,
        rgba(255, 255, 255, 0.1) 50%,
        rgba(255, 255, 255, 0.1) 75%,
        transparent 75%,
        transparent
      );
      background-size: 20px 20px;
      animation: move 2s linear infinite;
      @include border-radius(100px);
    }
  }

  .bar-text {
    position: absolute;
    width: 100%;
    height: 100%;
    color: var(--color-dark-shade-4, $color-dark-shade-4);
    padding-left: 10px;
    font-size: 10pt;
    font-weight: 500;
    z-index: 3;
    text-align: left;
    line-height: 30px;
    text-transform: lowercase;
  }
}

.short .box .bar-text,
.weak .box .bar-text,
.medium .box .bar-text,
.normal .box .bar-text,
.strong .box .bar-text,
.very-strong .box .bar-text {
  color: white;
}

.short .box .bar {
  background: var(--color-dark-shade-4, $color-dark-shade-4);
  @include border-radius(100px);
  width: 25%;
}

.weak .box .bar {
  background: var(--color-info-danger, $color-info-danger);
  @include border-radius(100px);
  width: 40%;
}

.medium .box .bar {
  background: var(--color-info-warning, $color-info-warning);
  @include border-radius(100px);
  width: 55%;
}

.normal .box .bar {
  background: var(--color-info-standard, $color-info-standard);
  @include border-radius(100px);
  width: 70%;
}

.strong .box .bar {
  background: var(--color-info-success, $color-info-success);
  @include border-radius(100px);
  width: 85%;
}

.very-strong .box .bar {
  background: var(--color-info-success, $color-info-success);
  @include border-radius(100px);
  width: 100%;
}
</style>
